// The MIT License (MIT)

// Copyright (c) 2017 Zalando SE

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import * as d3 from 'd3';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Config, ExtendedEntry } from '../models/models';

let counter = 0;
const Radar = ({ config, setActiveId }) => {
  const svgRef = useRef(null);
  const [canRun, setCanRun] = useState(true);

  const visualise = useCallback(
    (config: Config) => {
      let ring;
      let quadrant;
      let i;
      let entry: ExtendedEntry;
      let seed = 6;

      function random() {
        const x = Math.sin(seed++) * 10000;
        return x - Math.floor(x);
      }

      function random_between(min, max) {
        return min + random() * (max - min);
      }

      const quadrants = [];

      config.quadrants.forEach((quadrant, index) => {
        quadrants.push({ id: quadrant.id });
      });
      const rings = [];
      config.rings.forEach((ring, index) => {
        rings.push({ radius: (400 / config.rings.length) * (index + 1) });
      });

      function polar(cartesian) {
        const x = cartesian.x;
        const y = cartesian.y;
        return {
          t: Math.atan2(y, x),
          r: Math.sqrt(x * x + y * y),
        };
      }
      function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
        const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;

        return {
          x: centerX + radius * Math.cos(angleInRadians),
          y: centerY + radius * Math.sin(angleInRadians),
        };
      }

      function describeArc(x, y, radius, startAngle, endAngle) {
        const start = polarToCartesian(x, y, radius, endAngle);
        const end = polarToCartesian(x, y, radius, startAngle);

        const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

        const d = [
          'M',
          start.x,
          start.y,
          'A',
          radius,
          radius,
          0,
          largeArcFlag,
          0,
          end.x,
          end.y,
        ].join(' ');

        return d;
      }

      function cartesian(polar) {
        return {
          x: polar.r * Math.cos(polar.t),
          y: polar.r * Math.sin(polar.t),
        };
      }

      function bounded_interval(value, min, max) {
        const low = Math.min(min, max);
        const high = Math.max(min, max);
        return Math.min(Math.max(value, low), high);
      }

      function bounded_ring(polar, r_min, r_max) {
        return {
          t: polar.t,
          r: bounded_interval(polar.r, r_min, r_max),
        };
      }

      function segment() {
        return {
          clip: function (d) {
            const rMin = rings[d.ring - 1] ? rings[d.ring - 1].radius : 20;
            const rMax = rings[d.ring].radius;
            const ee = bounded_ring(polar(d), rMin + 12, rMax - 12);
            d.y = cartesian(ee).y;
            d.x = cartesian(ee).x;
            return d;
          },
        };
      }
      function randomIntFromInterval(min, max) {
        // min and max included
        return Math.random() * (max - min) + min;
      }
      // position each entry randomly in its segment
      for (i = 0; i < config.entries.length; i++) {
        entry = config.entries[i];
        entry.color = config.quadrants[entry.quadrant].color;
        entry.segment = segment();
        const rMin = rings[entry.ring - 1] ? rings[entry.ring - 1].radius : 20;
        const rMax = rings[entry.ring].radius;
        var aMin = (entry.quadrant * (Math.PI * 2)) / quadrants.length;
        var aMax = ((entry.quadrant + 1) * (Math.PI * 2)) / quadrants.length;
        const a = randomIntFromInterval(aMin + 0.2, aMax - 0.2);

        const r = random_between(rMin, rMax);
        entry.x = r * Math.cos(a);
        entry.y = r * Math.sin(a);
      }

      // partition entries according to segments
      const segmented = new Array(quadrants.length);
      for (quadrant = 0; quadrant < quadrants.length; quadrant++) {
        segmented[quadrant] = new Array(quadrants.length);

        for (ring = 0; ring < rings.length; ring++) {
          segmented[quadrant][ring] = [];
        }
      }
      for (i = 0; i < config.entries.length; i++) {
        entry = config.entries[i];
        entry.id = (i + 1).toString();
        segmented[entry.quadrant][entry.ring].push(entry);
      }

      function translate(x, y) {
        return 'translate(' + x + ',' + y + ')';
      }

      const svg = d3
        .select(svgRef.current)
        .style('background-color', config.colors.background)
        .attr('width', config.width)
        .attr('height', config.height);

      const radar = svg.append('g');
      radar.attr('transform', translate(config.width / 2, config.height / 2));

      const grid = radar.append('g');
      quadrants.forEach((quadrant, index) => {
        grid
          .append('line')
          .attr('x1', 0)
          .attr('y1', 0)
          .attr(
            'x2',
            400 * Math.cos((((Math.PI / 180) * 360) / quadrants.length) * (index + 1))
          )
          .attr(
            'y2',
            400 * Math.sin((((Math.PI / 180) * 360) / quadrants.length) * (index + 1))
          )
          .style('stroke', config.colors.grid)
          .style('stroke-width', 1);
      });

      // draw rings
      for (i = 0; i < rings.length; i++) {
        grid
          .append('circle')
          .attr('cx', 0)
          .attr('cy', 0)
          .attr('r', rings[i].radius)
          .style('fill', 'none')
          .style('stroke', config.colors.grid)
          .style('stroke-width', 1);
      }

      // legend
      const legend = radar.append('g');
      for (ring = 0; ring < rings.length; ring++) {
        legend
          .append('text')
          .attr('transform', translate(rings[ring].radius - 200 / rings.length, -10))
          .text(config.rings[ring].name)
          .style('font-family', 'Montserrat, Helvetica')
          .style('font-size', 74 / 4)
          .style('font-weight', '700')
          .style('fill', '#9b9b9b')
          .style('pointer-events', 'none')
          .style('user-select', 'none')
          .attr('text-anchor', 'middle');
      }

      const path1 = describeArc(0, 0, 425, 0, 180);
      const path2 = describeArc(0, 0, 410, 180, 0);

      legend
        .append('path')
        .attr('d', path1)

        .style('fill', 'none')
        .style('stroke', 'none')
        .style('stroke-width', 1)
        .attr('id', 'outerRing1')
        .attr('transform', `rotate(90)`);

      legend
        .append('path')
        .attr('d', path2)

        .style('fill', 'none')
        .style('stroke', 'none')
        .style('stroke-width', 1)
        .attr('id', 'outerRing2')
        .attr('transform', `rotate(270), scale(-1,+1)`);

      for (i = 0; i < quadrants.length; i++) {
        const item = legend
          .append('text')
          .append('textPath') //append a textPath to the text element
          .text(config.quadrants[i].name)
          .style('font-family', 'Montserrat, Helvetica')
          .style('font-size', 16)
          .style('font-weight', '700')
          .style('fill', '#9B9B9B')
          // .style('fill', config.quadrants[i].color)
          .style('pointer-events', 'none')
          .style('user-select', 'none')
          .attr('text-anchor', 'middle');
        if (i < quadrants.length / 2) {
          item.attr('xlink:href', '#outerRing1');
          item.attr(
            'startOffset',
            `${
              100 - ((i % 3) * ((100 / quadrants.length) * 2) + 100 / quadrants.length)
            }%`
          );
        } else {
          item.attr('xlink:href', '#outerRing2');
          item.attr(
            'startOffset',
            `${(i % 3) * ((100 / quadrants.length) * 2) + 100 / quadrants.length}%`
          );
        }
      }

      // layer for entries
      const rink = radar.append('g').attr('id', 'rink');

      // rollover bubble (on top of everything else)
      const bubble = radar
        .append('g')
        .attr('id', 'bubble')
        .attr('x', 0)
        .attr('y', 0)
        .style('opacity', 0)
        .style('pointer-events', 'none')
        .style('user-select', 'none');
      bubble.append('rect').attr('rx', 4).attr('ry', 4).style('fill', '#333');
      bubble
        .append('text')
        .style('font-family', 'sans-serif')
        .style('font-size', '10px')
        .style('fill', '#fff');
      bubble.append('path').attr('d', 'M 0,0 10,0 5,8 z').style('fill', '#333');

      function showBubble(e, d) {
        const blips = rink.selectAll('.blip').filter(function (de, i) {
          return de !== d;
        });
        blips.transition().attr('opacity', 0.4).duration(350);

        const tooltip = d3.select('#bubble text').text(d.label);
        const node = tooltip.node() as SVGGraphicsElement;

        const bbox = node.getBBox();
        d3.select('#bubble')
          .attr('transform', translate(d.x - bbox.width / 2, d.y - 16))
          .style('opacity', 0.8);
        d3.select('#bubble rect')
          .attr('x', -5)
          .attr('y', -bbox.height)
          .attr('width', bbox.width + 10)
          .attr('height', bbox.height + 4);
        d3.select('#bubble path').attr('transform', translate(bbox.width / 2 - 5, 3));
      }

      function setActive(e, d) {
        setActiveId(d.id);
      }

      function hideBubble(d) {
        setActiveId(0);
        rink.selectAll('.blip').transition().attr('opacity', 1).duration(350);

        d3.select('#bubble').attr('transform', translate(0, 0)).style('opacity', 0);
      }

      // draw blips on radar
      const blips = rink
        .selectAll('.blip')
        .data(config.entries)
        .enter()
        .append('g')
        .attr('class', 'blip')
        .on('mouseover', showBubble)
        .on('mouseout', hideBubble)
        .on('mouseup', setActive);

      // configure each blip
      blips.each(function (d: ExtendedEntry) {
        const blip = d3.select(this);
        blip.style('cursor', 'pointer');
        blip.transition().attr('opacity', 1).duration(250);

        if (d.moved > 0) {
          blip
            .append('path')
            .attr('d', 'M -11,5 11,5 0,-13 z') // triangle pointing up
            .style('fill', d.color);
        } else if (d.moved < 0) {
          blip
            .append('path')
            .attr('d', 'M -11,-5 11,-5 0,13 z') // triangle pointing down
            .style('fill', d.color);
        } else {
          blip.append('circle').attr('r', 10).attr('fill', d.color);
        }

        const blip_text = d.id;
        blip
          .append('text')
          .text(blip_text)
          .attr('y', 4)
          .attr('text-anchor', 'middle')
          .style('fill', '#fff')
          .style('font-family', 'Arial, Helvetica')
          .style('font-size', function (d) {
            return blip_text.toString().length > 2 ? '10' : '11';
          })
          .style('pointer-events', 'none')
          .style('user-select', 'none')
          .attr('id', d.id);
      });

      // make sure that blips stay inside their segment
      function ticked() {
        if (counter < 100) {
          counter++;
          blips.attr('transform', (d: ExtendedEntry) => {
            return translate(d.segment.clip(d).x, d.segment.clip(d).y);
          });
        }
      }

      // distribute blips, while avoiding collisions
      d3.forceSimulation()
        .nodes(config.entries as any)
        .velocityDecay(0.19)
        .force('collision', d3.forceCollide().radius(10).strength(0.05))
        .on('tick', ticked);

      setCanRun(false);
    },
    [setActiveId]
  );

  useEffect(() => {
    if (config && canRun) {
      visualise(config);
    }
  }, [canRun, config, visualise]);

  return <svg ref={svgRef} />;
};
export default Radar;
